/**
 * How we use a vendor: spending insights — TRBUD-466 Part 2.
 *
 * Three reads on a vendor's page, all for admins and managers only
 * (`manageVendors`), narrower than Lines supplied, which also lets processors in:
 *
 *   - `GET /vendors/:id/insights`            spend and visits over time, how often
 *     we go, what we buy, where, and the labor rate;
 *   - `GET /vendors/:id/insights/item?key=`  one item's price over time, units kept
 *     apart, beside the same item at other vendors;
 *   - `GET /vendors/compare?ids=`            up to four vendors side by side.
 *
 * Every figure comes from the money log (`v_report_money_log`), the view Lines
 * supplied and the report builder read, so the rules are theirs: drafts and voids
 * are out, reversals net to zero, money on cancelled jobs counts and is shown as
 * "of which on cancelled jobs", and MaintainX history counts in spend, visits and
 * frequency but not in items, prices or places.
 *
 * Eden's rulings (PLAN v2, 30 October 2026), which the shapes below carry:
 *   - A biller or shop switch, and the panel says which it counts. As the shop, a
 *     line another company billed is shown apart and never added (TRBUD-418).
 *   - A visit is the vendor page's repair history rule, and a ticket with child
 *     jobs counts once (the report builder's visit rule), so the panels agree.
 *   - Items group by catalog part, else the printed product code's key, else the
 *     line text tidied up, which is labelled "rough".
 *   - Prices are before bill discounts, units are never merged, and labor is a
 *     rate only where hours were stated.
 *   - The same item at other vendors is matched by part or printed code only.
 *   - Export sends a Money log definition to the report builder, as Lines
 *     supplied does.
 *
 * Browser-safe: zod, types, labels and pure functions. All money is USD.
 */
import { z } from "zod";
import { costDateSourceLabels, type CostDateSource } from "./cost-date.js";
import { isRealCalendarDay } from "./field-shapes.js";
import type { MatrixDefinition } from "./report-matrix.js";
import { SUPPLIED_LINE_ROLES, SUPPLIED_LINES_EXPORT_COLUMNS, type SuppliedLineRole } from "./vendor-supplied-lines.js";

/** Which side of a bill the vendor is read from: the company that billed, or the shop that did the work. */
export const VENDOR_INSIGHT_SIDES = SUPPLIED_LINE_ROLES;
export type VendorInsightSide = SuppliedLineRole;
export const VENDOR_INSIGHT_SIDE_LABEL: Record<VendorInsightSide, string> = {
  biller: "As the biller",
  shop: "As the shop that did the work",
};

/**
 * The window the figures cover.
 *   - `ytd`: January 1 to the fleet's today (the Spend YTD window), the default.
 *   - `last12`: the current calendar month and the eleven before it.
 *   - `all`: every dated line up to today.
 *   - `custom`: `from` and `to`, both required.
 */
export const VENDOR_INSIGHT_RANGES = ["ytd", "last12", "all", "custom"] as const;
export type VendorInsightRange = (typeof VENDOR_INSIGHT_RANGES)[number];
export const VENDOR_INSIGHT_RANGE_LABEL: Record<VendorInsightRange, string> = {
  ytd: "Year to date",
  last12: "Last 12 months",
  all: "All time",
  custom: "Dates",
};

/** At most four vendors side by side. */
export const VENDOR_COMPARE_MAX = 4;
/** An item key is `part:`, `code:` or `text:` and a bounded value. */
export const VENDOR_INSIGHT_ITEM_KEY_MAX = 300;
/** Top items listed by count and by spend. */
export const VENDOR_INSIGHT_TOP_ITEMS = 20;

const fleetDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(isRealCalendarDay, "expected a real calendar day");

const windowShape = {
  side: z.enum(VENDOR_INSIGHT_SIDES).default("biller"),
  range: z.enum(VENDOR_INSIGHT_RANGES).default("ytd"),
  from: fleetDay.optional(),
  to: fleetDay.optional(),
};

function checkWindow(value: { range: VendorInsightRange; from?: string | undefined; to?: string | undefined }, context: z.RefinementCtx): void {
  if (value.range === "custom") {
    if (value.from === undefined) context.addIssue({ code: "custom", path: ["from"], message: "A range of dates needs a start." });
    if (value.to === undefined) context.addIssue({ code: "custom", path: ["to"], message: "A range of dates needs an end." });
    if (value.from !== undefined && value.to !== undefined && value.from > value.to) {
      context.addIssue({ code: "custom", path: ["from"], message: "The start of the range is after its end." });
    }
  } else if (value.from !== undefined || value.to !== undefined) {
    context.addIssue({ code: "custom", path: ["range"], message: "Dates are only read with range=custom." });
  }
}

/** `GET /vendors/:id/insights`. */
export const vendorInsightsQuery = z.object(windowShape).strict().superRefine(checkWindow);
export type VendorInsightsQuery = z.infer<typeof vendorInsightsQuery>;

/** An item's kind: a catalog part, a printed product code, or tidied line text ("rough"). */
export const VENDOR_INSIGHT_ITEM_KINDS = ["part", "code", "text"] as const;
export type VendorInsightItemKind = (typeof VENDOR_INSIGHT_ITEM_KINDS)[number];

const itemKey = z
  .string()
  .min(6)
  .max(VENDOR_INSIGHT_ITEM_KEY_MAX)
  .refine((value) => parseInsightItemKey(value) !== null, "expected part:<id>, code:<code> or text:<words>");

/** `GET /vendors/:id/insights/item`. */
export const vendorInsightItemQuery = z.object({ ...windowShape, key: itemKey }).strict().superRefine(checkWindow);
export type VendorInsightItemQuery = z.infer<typeof vendorInsightItemQuery>;

// Bounded well above four ids (36 characters and a comma each), so a fifth id
// is refused by the count, in words, rather than by the length.
const vendorIds = z
  .string()
  .max(1000)
  .transform((value) => value.split(",").map((part) => part.trim()).filter((part) => part !== ""))
  .pipe(
    z.array(z.string().uuid())
      .min(1, "Name at least one vendor.")
      .max(VENDOR_COMPARE_MAX, `Compare at most ${VENDOR_COMPARE_MAX} vendors.`)
      .refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, "A vendor is named twice."),
  );

/** `GET /vendors/compare`. `ids` is comma-separated, one to four vendors, each once. */
export const vendorCompareQuery = z.object({ ...windowShape, ids: vendorIds }).strict().superRefine(checkWindow);
export type VendorCompareQuery = z.infer<typeof vendorCompareQuery>;

/**
 * An item key, read. `part:` carries a part id; `code:` a printed code's
 * normalised key (ASCII letters and digits, upper case, as the stored key);
 * `text:` the line text's words, upper case, one space apart.
 */
export function parseInsightItemKey(key: string): { kind: VendorInsightItemKind; value: string } | null {
  const colon = key.indexOf(":");
  if (colon <= 0) return null;
  const kind = key.slice(0, colon);
  const value = key.slice(colon + 1);
  if (value === "" || value.length > VENDOR_INSIGHT_ITEM_KEY_MAX) return null;
  if (kind === "part") {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ? { kind, value: value.toLowerCase() } : null;
  }
  if (kind === "code") return /^[A-Z0-9]+$/.test(value) ? { kind, value } : null;
  if (kind === "text") return /^[A-Z0-9]+( [A-Z0-9]+)*$/.test(value) ? { kind, value } : null;
  return null;
}

/** The key for an item. */
export function insightItemKey(kind: VendorInsightItemKind, value: string): string {
  return `${kind}:${value}`;
}

/** The window the figures cover, as the server applied it. `from` is null for all time. */
export interface VendorInsightWindow {
  range: VendorInsightRange;
  from: string | null;
  to: string;
  /** The Spend YTD window: as the biller, the spend equals Spend YTD on the Vendors list. */
  yearToDate: boolean;
}

export interface VendorInsightAmount {
  amount: string;
  lines: number;
}

export interface VendorInsightsTotals {
  /** As the biller, its spend; as the shop, its own bills only. */
  spend: string;
  lines: number;
  /** Of which on jobs that were cancelled: counted in `spend`, shown apart. */
  onCancelledJobs: string;
  /** Of which MaintainX history: counted in `spend`. */
  maintainx: string;
  /** As the shop: work it did on another company's bill. Shown apart, never added. Null as the biller. */
  billedByOthers: VendorInsightAmount | null;
  /** Lines whose day is not known, over all time: one separate total, in no month and no window. */
  noKnownDay: VendorInsightAmount;
}

export interface VendorInsightMonth {
  /** `YYYY-MM`, a fleet calendar month of the cost day. */
  month: string;
  spend: string;
  onCancelledJobs: string;
  /** Visits opened in the month. */
  visits: number;
  /** Spend change from the month before, in this window; null for the first month. */
  change: string | null;
}

export interface VendorInsightFrequency {
  /** Times used: visits opened in the window. */
  visits: number;
  /** Of which the visit's job was cancelled. */
  cancelledVisits: number;
  /** Of which MaintainX history. */
  maintainxVisits: number;
  firstDay: string | null;
  lastDay: string | null;
  /** Days between consecutive visit days; null with fewer than two visit days. */
  averageGapDays: number | null;
  medianGapDays: number | null;
  /** Visits divided by the calendar months the window covers; null with no visits. */
  visitsPerMonth: number | null;
  /** The calendar months that division counted. */
  months: number;
}

export interface VendorInsightItem {
  key: string;
  kind: VendorInsightItemKind;
  /** The part's name, the printed code, or the newest line text for a rough group. */
  label: string;
  /** Grouped by tidied line text, not by a part or a code: a rough grouping. */
  rough: boolean;
  /** The printed code, when the group is a code, or the newest one seen. */
  printedCode: string | null;
  lines: number;
  spend: string;
  lastDay: string | null;
  /** The units it was bought in, never merged. "" is "unit not stated". */
  units: string[];
}

export interface VendorInsightItems {
  byCount: VendorInsightItem[];
  bySpend: VendorInsightItem[];
  /** TruckBuddy part and labor lines in the window, reversed lines left out. */
  linesConsidered: number;
  /** Lines with no part, no code and no text: they belong to no item. */
  ungroupedLines: number;
}

export interface VendorInsightLabourMonth {
  month: string;
  hours: string;
  amount: string;
  /** Amount divided by hours, to the cent. */
  rate: string;
}

export interface VendorInsightLabour {
  months: VendorInsightLabourMonth[];
  /** Labor lines with stated hours: the ones a rate is read from. */
  hourlyLines: number;
  /** Labor lines with no stated hours: flat-rate charges, never turned into a rate. */
  flatRateLines: number;
}

export const VENDOR_INSIGHT_PLACE_BASES = ["shop", "branch", "none"] as const;
export type VendorInsightPlaceBasis = (typeof VENDOR_INSIGHT_PLACE_BASES)[number];

export interface VendorInsightPlace {
  label: string;
  /** `shop`: the shop recorded on the bill; `branch`: the branch printed on the receipt, as printed; `none`: not recorded. */
  basis: VendorInsightPlaceBasis;
  spend: string;
  lines: number;
  jobs: number;
}

export interface VendorInsightTerminal {
  /** The truck's home terminal, or null when none is recorded. */
  label: string | null;
  spend: string;
  lines: number;
  jobs: number;
}

export interface VendorInsightOpenedPlace {
  /** Samsara's place name where the job was opened, or null when none was recorded. */
  label: string | null;
  jobs: number;
}

export interface VendorInsightPlaces {
  workDoneAt: VendorInsightPlace[];
  homeTerminals: VendorInsightTerminal[];
  /** Jobs opened in TruckBuddy only; an imported job has no place it was opened. */
  openedAt: VendorInsightOpenedPlace[];
}

export interface VendorInsightSeasonalMonth {
  /** 1 to 12. */
  calendarMonth: number;
  label: string;
  /** Distinct years with spend in this calendar month: two or more. */
  years: number;
  averageSpend: string;
  averageVisits: number;
}

export interface VendorInsightSeasonal {
  /** Only calendar months seen in two or more years. Empty: not enough history yet. */
  months: VendorInsightSeasonalMonth[];
  /** Distinct years with any dated spend, over all time. */
  yearsSeen: number;
}

export interface VendorInsightCostDaySource {
  source: CostDateSource;
  label: string;
  lines: number;
  amount: string;
}

/** What `GET /vendors/:id/insights` answers. */
export interface VendorInsights {
  vendorId: string;
  vendorName: string;
  side: VendorInsightSide;
  window: VendorInsightWindow;
  totals: VendorInsightsTotals;
  /** Every calendar month of the window (from the first dated line for all time). */
  history: VendorInsightMonth[];
  frequency: VendorInsightFrequency;
  items: VendorInsightItems;
  labour: VendorInsightLabour;
  places: VendorInsightPlaces;
  /** Over all dated history on this side, not only the window. */
  seasonal: VendorInsightSeasonal;
  /** Where the counted lines' days came from (`cost_date_source`). */
  costDaySources: VendorInsightCostDaySource[];
  currency: "USD";
}

export interface VendorInsightPricePoint {
  day: string;
  /** The unit price: the stated unit cost, else amount over quantity; for labor, the hourly rate. Four places. */
  price: string;
  quantity: string | null;
  amount: string;
  costEntryId: string;
  workOrderId: string;
  workOrderLabel: string;
}

export interface VendorInsightPriceMonth {
  month: string;
  /** The mean of the month's unit prices, four places. */
  averagePrice: string;
  lines: number;
  /** From the month before it was seen in, four places; null for the first. */
  change: string | null;
  /** That change as a percentage, one place; null for the first. */
  changePercent: number | null;
}

export interface VendorInsightPriceSeries {
  /** The unit as stored, lower case and trimmed; "" is "unit not stated", "hour" for labor rates. */
  unit: string;
  unitLabel: string;
  points: VendorInsightPricePoint[];
  months: VendorInsightPriceMonth[];
}

export interface VendorInsightOtherVendor {
  vendorId: string;
  vendorName: string;
  unit: string;
  unitLabel: string;
  lines: number;
  averagePrice: string;
  lastPrice: string;
  lastDay: string;
}

/** What `GET /vendors/:id/insights/item` answers. */
export interface VendorInsightItemDetail {
  vendorId: string;
  vendorName: string;
  side: VendorInsightSide;
  window: VendorInsightWindow;
  key: string;
  kind: VendorInsightItemKind;
  label: string;
  rough: boolean;
  lines: number;
  spend: string;
  /** One series per unit: units are never merged. */
  series: VendorInsightPriceSeries[];
  /** Lines with no unit price to read (no unit cost and no quantity, or labor with no hours). */
  unpricedLines: number;
  /** The same part or printed code at other vendors (as the biller), in the window. Null for a rough item: text is never matched. */
  otherVendors: VendorInsightOtherVendor[] | null;
  currency: "USD";
}

export interface VendorCompareRow {
  vendorId: string;
  vendorName: string;
  spend: string;
  lines: number;
  onCancelledJobs: string;
  billedByOthers: string | null;
  visits: number;
  firstDay: string | null;
  lastDay: string | null;
  averageGapDays: number | null;
  medianGapDays: number | null;
  visitsPerMonth: number | null;
  /** Spend by month, one entry per month in `VendorCompare.months`. */
  monthly: string[];
}

/** What `GET /vendors/compare` answers: one side and one window for every vendor. */
export interface VendorCompare {
  side: VendorInsightSide;
  window: VendorInsightWindow;
  months: string[];
  vendors: VendorCompareRow[];
  currency: "USD";
}

/** The word for where a line's day came from. */
export function costDaySourceLabel(source: CostDateSource): string {
  return costDateSourceLabels[source];
}

/**
 * The report-builder definition behind the panel's Export: the vendor as biller
 * or as shop over the panel's window, so its rows are the lines the spend is
 * read from. Year to date exports as the 'ytd' timeframe; any other window as a
 * range on the cost date (with no start for all time).
 */
export function insightsExportDefinition(
  vendorId: string,
  view: { side: VendorInsightSide; window: VendorInsightWindow },
): MatrixDefinition {
  const filters: MatrixDefinition["filters"] = [
    { field: view.side === "shop" ? "money_log.provider_vendor.vendor_id" : "money_log.line_vendor.vendor_id", operator: "eq", value: vendorId },
  ];
  if (!view.window.yearToDate) {
    filters.push(view.window.from === null
      ? { field: "money_log.cost_date", operator: "range", to: view.window.to }
      : { field: "money_log.cost_date", operator: "range", from: view.window.from, to: view.window.to });
  }
  return {
    version: 2,
    base: "money_log",
    mode: "detail",
    columns: SUPPLIED_LINES_EXPORT_COLUMNS.map((field) => ({ field })),
    filters,
    timeframe: view.window.yearToDate ? "ytd" : "all",
    shareMode: "full",
    sort: [{ column: 0, direction: "desc" }],
  };
}
